import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { WEB_MODAL_MAX_WIDTH } from '../../theme/webLayoutWidths';

import {
  StyleSheet,
  View,
  Keyboard,
  Modal,
  Platform,
  Pressable,
  useWindowDimensions,
} from 'react-native';
import { Button, Text } from 'react-native-paper';

import {
  AppAlert,
  type AppAlertPayload,
  type AppAlertButton,
} from '../../services/AppAlert';
import { GTFonts, useTheme } from '../../theme';
import { fs, s, v } from '../../utils/libs';
import { shouldUseWidthConstraint } from '../../utils/layoutUtils';

const isWeb = Platform.OS === 'web';

const getButtonVariant = (style?: AppAlertButton['style']) => {
  switch (style) {
    case 'destructive':
      return 'destructive';
    case 'cancel':
      return 'cancel';
    default:
      return 'default';
  }
};

type AppAlertHostProps = {
  useCustomAlerts?: boolean;
};

const AppAlertHost: React.FC<AppAlertHostProps> = ({ useCustomAlerts }) => {
  const { theme } = useTheme();
  const { width, height } = useWindowDimensions();
  const constrainWidth = shouldUseWidthConstraint(width, height);
  const [visible, setVisible] = useState(false);
  const [payload, setPayload] = useState<AppAlertPayload | null>(null);
  const cleanupRef = useRef<null | (() => void)>(null);

  useEffect(() => {
    if (!useCustomAlerts) return;
    cleanupRef.current = AppAlert.registerHost(data => {
      Keyboard.dismiss();
      requestAnimationFrame(() => {
        setPayload(data);
        setVisible(true);
      });
    });
    return () => {
      if (cleanupRef.current) cleanupRef.current();
    };
  }, [useCustomAlerts]);

  // RN Web Modal is position:fixed; also lock document scroll so long forms
  // cannot move under the overlay.
  useEffect(() => {
    if (!isWeb || !visible) return;
    const html = document.documentElement;
    const body = document.body;
    const prevHtmlOverflow = html.style.overflow;
    const prevBodyOverflow = body.style.overflow;
    html.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    return () => {
      html.style.overflow = prevHtmlOverflow;
      body.style.overflow = prevBodyOverflow;
    };
  }, [visible]);

  const onDismiss = useCallback(() => {
    setVisible(false);
    if (payload?.options?.onDismiss) payload.options.onDismiss();
  }, [payload]);

  const buttons = useMemo(() => payload?.buttons ?? [], [payload]);
  const cancelable = payload?.options?.cancelable !== false;

  if (!useCustomAlerts || !payload) return null;

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={cancelable ? onDismiss : undefined}
      statusBarTranslucent>
      <View style={styles.backdropRoot}>
        <Pressable
          style={StyleSheet.absoluteFill}
          onPress={cancelable ? onDismiss : undefined}
          accessibilityRole="button"
          accessibilityLabel="Dismiss alert"
        />
        <View
          style={[
            styles.card,
            { backgroundColor: theme.colors.surface },
            constrainWidth ? styles.cardConstrained : null,
          ]}
          accessibilityRole="alert">
          <Text style={[styles.title, { color: theme.colors.text }]}>
            {payload.title}
          </Text>
          {payload.message ? (
            <Text
              style={[styles.message, { color: theme.colors.textSecondary }]}>
              {payload.message}
            </Text>
          ) : null}
          <View style={styles.actions}>
            {buttons.map((btn, idx) => {
              const variant = getButtonVariant(btn.style);
              const isPositiveDefault =
                variant === 'default' && idx === buttons.length - 1;
              const color =
                variant === 'destructive'
                  ? theme.colors.error
                  : variant === 'cancel'
                    ? theme.colors.textSecondary
                    : isPositiveDefault
                      ? theme.colors.success
                      : theme.colors.primary;
              return (
                <Button
                  key={`${btn.text}-${idx}`}
                  onPress={() => {
                    setVisible(false);
                    requestAnimationFrame(() => btn.onPress?.());
                  }}
                  textColor={color}
                  style={styles.actionButton}>
                  {btn.text}
                </Button>
              );
            })}
          </View>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  backdropRoot: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: s(24),
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  card: {
    borderRadius: s(12),
    paddingTop: v(20),
    paddingBottom: v(8),
    paddingHorizontal: s(8),
    width: '100%',
    maxWidth: 560,
    zIndex: 1,
    // Keep the card above the dismiss Pressable on web.
    ...(isWeb ? ({ position: 'relative' } as const) : null),
  },
  cardConstrained: {
    maxWidth: WEB_MODAL_MAX_WIDTH,
    width: '90%',
  },
  title: {
    fontFamily: GTFonts.bold,
    fontSize: fs(18),
    lineHeight: fs(22),
    paddingHorizontal: s(16),
    marginBottom: v(8),
  },
  message: {
    fontFamily: GTFonts.regular,
    fontSize: fs(14),
    lineHeight: fs(20),
    paddingHorizontal: s(16),
    marginBottom: v(8),
  },
  actions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    flexWrap: 'wrap',
    paddingHorizontal: s(8),
    paddingTop: v(4),
  },
  actionButton: {
    marginLeft: s(6),
  },
});

export default AppAlertHost;
